import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ChevronLeft, Send, CheckCheck, Loader2, Star, Sparkles, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { SummaryGeneratorButton, BiasLensButton, ReviewQualityButton } from "@/components/AIAssistants";
import { PullFromWinsButton, PullFromCoachingButton } from "@/components/ReviewPullButtons";

const STATUS_META = {
  self_pending: { label: "Self-assessment due", style: "bg-amber-50 text-amber-700 border-amber-200" },
  manager_pending: { label: "Manager review", style: "bg-blue-50 text-blue-700 border-blue-200" },
  joint_pending: { label: "Joint sign-off", style: "bg-violet-50 text-violet-700 border-violet-200" },
  signed_off: { label: "Signed off", style: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export default function ReviewDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [r, setR] = useState(null);
  const [acting, setActing] = useState(false);

  const reload = async () => {
    const res = await api.get(`/reviews/${id}`);
    setR(res.data);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  const isOwner = user?.employee_id && r && r.employee_id === user.employee_id;
  const isManager = user?.employee_id && r && r.primary_reviewer_id === user.employee_id;
  const fw = r?.framework_snapshot || {};
  const ratingLevels = fw?.rating_scale?.levels || [];
  const values = fw?.values_assessment?.values || [];

  const isMyTurn = useMemo(() => {
    if (!r) return false;
    if (r.status === "self_pending" && isOwner) return "self";
    if (r.status === "manager_pending" && isManager) return "manager";
    return false;
  }, [r, isOwner, isManager]);

  const updateLocal = (path, value) => {
    setR((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      const segs = path.split(".");
      let cur = copy;
      for (let i = 0; i < segs.length - 1; i++) cur = cur[segs[i]];
      cur[segs[segs.length - 1]] = value;
      return copy;
    });
  };

  const save = async () => {
    if (!isMyTurn) return;
    setActing(true);
    try {
      const url = `/reviews/${id}/${isMyTurn}`;
      const body = isMyTurn === "self"
        ? {
            objective_assessments: r.objective_assessments,
            value_assessments: r.value_assessments,
            overall_self_summary: r.overall_self_summary,
            overall_self_rating: r.overall_self_rating,
          }
        : {
            objective_assessments: r.objective_assessments,
            value_assessments: r.value_assessments,
            overall_manager_summary: r.overall_manager_summary,
            overall_manager_rating: r.overall_manager_rating,
          };
      await api.patch(url, body);
      toast.success("Saved");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setActing(false);
    }
  };

  const submit = async () => {
    if (!isMyTurn) return;
    setActing(true);
    try {
      // Save first, then submit
      await save();
      await api.post(`/reviews/${id}/submit`, { role: isMyTurn });
      toast.success(isMyTurn === "self" ? "Self-assessment submitted to your manager" : "Manager assessment submitted for joint sign-off");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally {
      setActing(false);
    }
  };

  const sign = async () => {
    setActing(true);
    try {
      const role = isOwner ? "employee" : isManager ? "manager" : null;
      if (!role) return;
      await api.post(`/reviews/${id}/sign`, { role });
      toast.success("Signed");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign failed");
    } finally {
      setActing(false);
    }
  };

  if (!r) return <div className="text-sm text-slate-500">Loading review…</div>;

  const meta = STATUS_META[r.status] || { label: r.status, style: "bg-slate-100 text-slate-600 border-slate-200" };
  const editable = !!isMyTurn;
  const canSign = r.status === "joint_pending" && (isOwner || isManager);
  const empSigned = !!r.sign_off?.employee_signed_at;
  const mgrSigned = !!r.sign_off?.manager_signed_at;

  const mySigned = isOwner ? empSigned : isManager ? mgrSigned : false;

  return (
    <div className="max-w-5xl space-y-6" data-testid="review-detail-page">
      <Link to="/reviews" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5">
        <ChevronLeft className="w-3 h-3" /> Back to reviews
      </Link>

      <div className="axia-card p-6 lg:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <span className={`axia-pill ${meta.style}`}>{meta.label}</span>
              <span className="axia-overline">{r.phase.replace("_", "-")}</span>
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-2">
              {isOwner ? "Your review" : `Review for ${r.employee_name}`}
            </h1>
            <p className="text-sm text-slate-500 mt-1.5">{fw?.name}</p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-500">
              {empSigned && <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCheck className="w-3 h-3" /> Employee signed</span>}
              {mgrSigned && <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCheck className="w-3 h-3" /> Manager signed</span>}
            </div>
          </div>
          {(isOwner || isManager) && r.status !== "self_pending" && (
            <ReviewQualityButton reviewId={id} />
          )}
        </div>
      </div>

      {/* Objectives */}
      <Section title={`Objectives (${(r.objective_assessments || []).length})`} subtitle="Rate each objective. Manager rating overrides on disagreement.">
        <div className="space-y-4">
          {(r.objective_assessments || []).map((a, i) => {
            const obj = (r.objectives || []).find((o) => o.id === a.objective_id) || {};
            return (
              <div key={a.objective_id} className="border border-slate-200 rounded-md p-4 bg-white" data-testid={`obj-assess-${a.objective_id}`}>
                <div className="font-display text-sm font-semibold text-slate-900 leading-snug">
                  {obj.fields?.statement || obj.fields?.objective || "Objective"}
                </div>
                <div className="text-xs text-slate-500 mt-1">weight {obj.weightage}%</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                  <RatingPicker
                    label="Self rating"
                    value={a.self_rating}
                    levels={ratingLevels}
                    disabled={!isOwner || r.status !== "self_pending"}
                    onChange={(v) => updateLocal(`objective_assessments.${i}.self_rating`, v)}
                    testid={`self-rating-${a.objective_id}`}
                  />
                  <RatingPicker
                    label="Manager rating"
                    value={a.manager_rating}
                    levels={ratingLevels}
                    disabled={!isManager || r.status !== "manager_pending"}
                    onChange={(v) => updateLocal(`objective_assessments.${i}.manager_rating`, v)}
                    testid={`mgr-rating-${a.objective_id}`}
                  />
                  <Textarea
                    placeholder="Self commentary"
                    value={a.self_commentary || ""}
                    onChange={(e) => updateLocal(`objective_assessments.${i}.self_commentary`, e.target.value)}
                    disabled={!isOwner || r.status !== "self_pending"}
                    rows={2}
                  />
                  <Textarea
                    placeholder="Manager commentary"
                    value={a.manager_commentary || ""}
                    onChange={(e) => updateLocal(`objective_assessments.${i}.manager_commentary`, e.target.value)}
                    disabled={!isManager || r.status !== "manager_pending"}
                    rows={2}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Values */}
      {values.length > 0 && (
        <Section title={`Values (${values.length})`} subtitle={`Contributes ${fw.final_rating_composition?.values_weight_pct || 0}% to final rating`}>
          <div className="space-y-3">
            {(r.value_assessments || []).map((v, i) => {
              const def = values.find((x) => x.id === v.value_id) || {};
              return (
                <div key={v.value_id} className="border border-slate-200 rounded-md p-4 bg-white">
                  <div className="font-display text-sm font-semibold text-slate-900">{def.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{def.description}</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <RatingPicker
                      label="Self rating"
                      value={v.self_rating}
                      levels={ratingLevels}
                      disabled={!isOwner || r.status !== "self_pending"}
                      onChange={(val) => updateLocal(`value_assessments.${i}.self_rating`, val)}
                    />
                    <RatingPicker
                      label="Manager rating"
                      value={v.manager_rating}
                      levels={ratingLevels}
                      disabled={!isManager || r.status !== "manager_pending"}
                      onChange={(val) => updateLocal(`value_assessments.${i}.manager_rating`, val)}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* Overall summary */}
      <Section title="Overall summary">
        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between gap-2">
              <Label>Self summary</Label>
              <div className="flex items-center gap-2">
                {isOwner && r.status === "self_pending" && (
                  <PullFromWinsButton
                    cycleId={r.cycle_id}
                    onApply={(text) => updateLocal("overall_self_summary", text)}
                  />
                )}
                {isOwner && r.status === "self_pending" && (
                  <SummaryGeneratorButton
                    reviewId={id}
                    role="self"
                    onApply={(text) => updateLocal("overall_self_summary", text)}
                  />
                )}
                {isOwner && r.status === "self_pending" && r.overall_self_summary && (
                  <BiasLensButton
                    text={r.overall_self_summary}
                    onSuggest={(phrase, suggestion) =>
                      updateLocal("overall_self_summary", (r.overall_self_summary || "").replaceAll(phrase, suggestion))
                    }
                  />
                )}
              </div>
            </div>
            <Textarea
              value={r.overall_self_summary || ""}
              onChange={(e) => updateLocal("overall_self_summary", e.target.value)}
              disabled={!isOwner || r.status !== "self_pending"}
              rows={3}
              placeholder="Reflect on the period: what worked, what didn't, what you learned."
              data-testid="self-summary"
            />
            <div className="mt-2">
              <RatingPicker
                label="Overall self rating"
                value={r.overall_self_rating}
                levels={ratingLevels}
                disabled={!isOwner || r.status !== "self_pending"}
                onChange={(v) => updateLocal("overall_self_rating", v)}
                testid="overall-self-rating"
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between gap-2">
              <Label>Manager summary</Label>
              <div className="flex items-center gap-2">
                {isManager && r.status === "manager_pending" && (
                  <PullFromCoachingButton
                    subjectEmployeeId={r.employee_id}
                    cycleId={r.cycle_id}
                    onApply={(text) => updateLocal("overall_manager_summary", text)}
                  />
                )}
                {isManager && r.status === "manager_pending" && (
                  <SummaryGeneratorButton
                    reviewId={id}
                    role="manager"
                    onApply={(text) => updateLocal("overall_manager_summary", text)}
                  />
                )}
                {isManager && r.status === "manager_pending" && r.overall_manager_summary && (
                  <BiasLensButton
                    text={r.overall_manager_summary}
                    onSuggest={(phrase, suggestion) =>
                      updateLocal("overall_manager_summary", (r.overall_manager_summary || "").replaceAll(phrase, suggestion))
                    }
                  />
                )}
              </div>
            </div>
            <Textarea
              value={r.overall_manager_summary || ""}
              onChange={(e) => updateLocal("overall_manager_summary", e.target.value)}
              disabled={!isManager || r.status !== "manager_pending"}
              rows={3}
              placeholder="Manager's narrative: outcomes, growth, focus areas."
              data-testid="manager-summary"
            />
            <div className="mt-2">
              <RatingPicker
                label="Overall manager rating"
                value={r.overall_manager_rating}
                levels={ratingLevels}
                disabled={!isManager || r.status !== "manager_pending"}
                onChange={(v) => updateLocal("overall_manager_rating", v)}
                testid="overall-manager-rating"
              />
            </div>
          </div>
        </div>
      </Section>

      <div className="sticky bottom-4">
        <div className="axia-card p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm text-slate-600">
            {editable && (isMyTurn === "self"
              ? "Save your draft, then submit to your manager."
              : "Review the employee's self-assessment, then submit for joint sign-off.")}
            {canSign && !mySigned && "Both summaries submitted. Sign to commit this review."}
            {r.status === "signed_off" && "Review signed off by both parties."}
          </div>
          <div className="flex gap-2">
            {editable && <Button variant="outline" onClick={save} disabled={acting} data-testid="save-review-btn">{acting && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />} Save draft</Button>}
            {editable && <Button onClick={submit} disabled={acting} data-testid="submit-review-btn"><Send className="w-3.5 h-3.5 mr-2" /> Submit</Button>}
            {canSign && !mySigned && <Button onClick={sign} disabled={acting} data-testid="sign-review-btn"><ShieldCheck className="w-3.5 h-3.5 mr-2" /> Sign</Button>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="axia-card p-6 space-y-4">
      <div>
        <h2 className="font-display text-base font-semibold text-slate-900">{title}</h2>
        {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
      </div>
      {children}
    </section>
  );
}

function RatingPicker({ label, value, levels, disabled, onChange, testid }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wide text-slate-500">{label}</Label>
      <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={testid}>
        {levels.map((l) => (
          <button
            key={l.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(l.value)}
            className={`px-2.5 py-1.5 rounded-md border text-xs ${
              value === l.value ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
            } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
            title={l.description || ""}
          >
            <span className="font-mono font-semibold">{l.value}</span> {l.label}
          </button>
        ))}
      </div>
    </div>
  );
}
